import { createContext } from 'react';
import { useCallback, useContext, useReducer, useState } from 'react';
import { groupByCondition } from 'app/agriculture/AgTimeline/utils/groupByCondition';
import { isOverlapping } from 'app/agriculture/AgTimeline/components/TimelinePellet/StackingContext/utils/isOverlapping';

type StackContainersDispatch =
    | { type: 'set'; id: string; ref: HTMLDivElement }
    | { type: 'delete'; id: string }
    | { type: 'clear' };

export function useStacking() {
    const [rows, setRows] = useState<{ ref: HTMLDivElement; id: string }[][]>([]);
    const [, dispatch] = useReducer(
        (currentRefs: Map<string, HTMLDivElement>, action: StackContainersDispatch) => {
            const newRefs = currentRefs;
            switch (action.type) {
                case 'set': {
                    newRefs.set(action.id, action.ref);
                    break;
                }
                case 'delete': {
                    newRefs.delete(action.id);
                    break;
                }
                case 'clear': {
                    newRefs.clear();
                    break;
                }
            }
            const mappedRefs = [...newRefs.entries()].map(([id, ref]) => ({ ref, id }));

            const newRows = groupByCondition(
                mappedRefs,
                ({ ref: ref1 }, { ref: ref2 }) =>
                    isOverlapping(ref1.getBoundingClientRect(), ref2.getBoundingClientRect(), {
                        offset: 8,
                    }),
                {
                    exclude: true,
                },
            );
            setRows(newRows);
            return newRefs;
        },
        new Map<string, HTMLDivElement>(),
    );

    const addContainer = useCallback((id: string, ref: HTMLDivElement) => {
        dispatch({ type: 'set', id, ref });
    }, []);

    const removeContainer = useCallback((id: string) => {
        dispatch({ type: 'delete', id });
    }, []);

    return {
        rows,
        addContainer,
        removeContainer,
    };
}

export const StackingContext = createContext<{
    rows: { ref: HTMLDivElement; id: string }[][];
    addContainer(id: string, ref: HTMLDivElement): void;
    removeContainer(id: string): void;
}>({ rows: [], addContainer() {}, removeContainer() {} });

export function useStackingContext() {
    return useContext(StackingContext);
}
